import * as React from "react";
import { FormattedMessage } from "react-intl";
import { ChevronLeftIcon, ChevronRightIcon, DotsHorizontalIcon } from "@/icons";

import { cn } from "@/utils/tailwind";
import { ButtonProps } from "@/kit/ui/button";
import { buttonVariants } from "@/kit/helpers/button";

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full", className)}
    {...props}
  />
);
(Pagination as React.FunctionComponent).displayName = "Pagination";

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex flex-row items-center gap-1 w-full", className)}
    {...props}
  />
));
(PaginationContent as React.FunctionComponent).displayName =
  "PaginationContent";

const PaginationSpacer = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("w-full", className)} {...props} />
));
(PaginationSpacer as React.FunctionComponent).displayName = "PaginationItem";

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
));
(PaginationItem as React.FunctionComponent).displayName = "PaginationItem";

type PaginationButtonProps = {
  isActive?: boolean;
} & Pick<ButtonProps, "size" | "disabled"> &
  React.ComponentProps<"button">;

const PaginationButton = ({
  className,
  isActive,
  size = "sm",
  ...props
}: PaginationButtonProps) => (
  <button
    aria-current={isActive ? "page" : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? "secondary" : "tertiary",
        hue: "grey",
        size,
      }),
      className,
    )}
    {...props}
  />
);
(PaginationButton as React.FunctionComponent).displayName = "PaginationLink";

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationButton>) => (
  <PaginationButton
    aria-label="Go to previous page"
    size="sm"
    className={cn("gap-1 pl-2.5", className)}
    {...props}
  >
    <ChevronLeftIcon className="h-4 w-4" />
    <span>
      <FormattedMessage defaultMessage="Previous" />
    </span>
  </PaginationButton>
);
(PaginationPrevious as React.FunctionComponent).displayName =
  "PaginationPrevious";

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationButton>) => (
  <PaginationButton
    aria-label="Go to next page"
    size="sm"
    className={cn("gap-1 pr-2.5", className)}
    {...props}
  >
    <span>
      <FormattedMessage defaultMessage="Next" />
    </span>
    <ChevronRightIcon className="h-4 w-4" />
  </PaginationButton>
);
(PaginationNext as React.FunctionComponent).displayName = "PaginationNext";

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <DotsHorizontalIcon className="h-4 w-4" />
    <span className="sr-only">
      <FormattedMessage defaultMessage="More Pages" />
    </span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export {
  Pagination,
  PaginationContent,
  PaginationButton,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationSpacer,
  PaginationEllipsis,
};
